// LineageEditor.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactFlow, {
  ReactFlowProvider,
  Background,
  Controls,
  Position,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  Node,
  Edge,
  MarkerType,
  NodeChange,
  EdgeChange,
  Connection,
} from "reactflow";
import "reactflow/dist/style.css";

import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Form,
  Button,
  Tabs,
  Tab,
  InputGroup,
  ListGroup,
} from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";

/* -------------------------
   Types
   ------------------------- */

type Severity = "info" | "warning" | "error";

type Note = {
  tag: string;
  severity: Severity;
  category?: string; // lineage | semantic | dq | standardization | lookup
  action?: string; // review | add_rule | harmonize | drop_mapping
  message: string;
};

type Mapping = {
  mdm_entity: string;
  mdm_field: string; // e.g. "OrganizationInfo-OrganizationName"
  source_table: string;
  source_column: string;
  source_type?: string;
  target_type?: string;
  confidence?: "High" | "Medium" | "Low" | string;
  notes?: Note[];
  id?: string;
};

interface LineageEditorProps {
  sourceJson?: any;
  targetJson?: any;
  lineage?: { mappings?: Mapping[]; suggested_additions?: any[]; semantic_issues?: any[] } | Mapping[];
  height?: number;
}

/* -------------------------
   Constants & sample data (demo)
   ------------------------- */

const TABLE_WIDTH = 260;
const HEADER_HEIGHT = 36;
const ROW_HEIGHT = 28;
const INNER_PADDING = 8;

/* Demo source JSON (multiple tables) */
const DEFAULT_SOURCE_JSON = {
  tables: [
    {
      name: "ORGANIZATION",
      columns: [
        { name: "ORG_NM", type: "object", pk: false, fk: false },
        { name: "TAX_ID", type: "integer" },
        { name: "TAX_ID_TP_CD", type: "object" },
        { name: "SIC_CD", type: "integer" },
        { name: "QBE_HASH_PRTY_ID", type: "string" },
      ],
    },
    {
      name: "PHYSICAL_ADDRESS",
      columns: [
        { name: "ADDR_1_TXT", type: "object" },
        { name: "CITY_NM", type: "object" },
        { name: "PSTL_CD", type: "object" },
      ],
    },
  ],
};

/* Demo target JSON: nested FieldGroups (MDM style) */
const DEFAULT_TARGET_JSON = {
  name: "Organization",
  id: "Organization",
  eClass: "FieldGroup",
  field: [
    {
      eClass: "Field",
      TBL_ID: "Organization",
      FLD_ID: "Organization-OrganizationId",
      COLUMN_NAME: "OrganizationId",
      DATA_TYP: "TextField",
      MANDATORY: "Y",
      DATA_LEN: "36",
    },
    {
      eClass: "FieldGroup",
      name: "OrganizationInfo",
      field: [
        {
          eClass: "Field",
          TABLE_NM: "OrganizationInfo",
          COLUMN_NAME: "OrganizationName",
          DATA_TYP: "TextField",
          MANDATORY: "Y",
          DATA_LEN: "255",
        },
        {
          eClass: "Field",
          TABLE_NM: "OrganizationInfo",
          COLUMN_NAME: "SicCode",
          DATA_TYP: "TextField",
        },
      ],
    },
  ],
};

/* Demo lineage (use this if no props provided) */
const DEFAULT_LINEAGE = {
  mappings: [
    {
      mdm_entity: "Organization",
      mdm_field: "OrganizationId",
      source_table: "ORGANIZATION",
      source_column: "QBE_HASH_PRTY_ID",
      source_type: "string",
      target_type: "string",
      confidence: "High",
      notes: [
        {
          tag: "potential_id",
          severity: "info" as Severity,
          message: "QBE_HASH_PRTY_ID is likely the unique identifier for the organization.",
        },
      ],
    },
    {
      mdm_entity: "Organization",
      mdm_field: "OrganizationInfo-OrganizationName",
      source_table: "ORGANIZATION",
      source_column: "ORG_NM",
      source_type: "string",
      target_type: "string",
      confidence: "High",
      notes: [
        {
          tag: "exact_match",
          severity: "info" as Severity,
          message: "Exact semantic match based on name",
        },
      ],
    },
  ],
  suggested_additions: [
    {
      source_table: "ORGANIZATION",
      source_column: "SIC_DESCRIPTON",
      recommended_field_name: "OrganizationInfo-SicDescription",
      recommended_type: "Text (100)",
      reason:
        "SIC Description is widely used in insurance for industry classification and risk assessment. Including it in MDM will improve underwriting, segmentation, and compliance reporting.",
      confidence: "High",
      notes: [{ tag: "missing_field", severity: "warning" as Severity, message: "Important source field not represented in MDM model" }],
    },
  ],
  semantic_issues: [
    {
      mdm_field: "OrganizationInfo-OrganizationType",
      source_mapping: ["LGL_ENT_CD"],
      issue: "Semantic mismatch: Legal Entity Code is not equivalent to Organization Type",
      suggestion:
        "Consider removing LGL_ENT_CD from OrganizationInfo-OrganizationType, or clarify the field's purpose. If Legal Entity is important, consider mapping it to LegalEntityCode instead.",
      notes: [{ tag: "semantic_mismatch", severity: "error" as Severity, message: "Source column meaning does not align with target field" }],
    },
  ],
};

/* -------------------------
   Utility helpers
   ------------------------- */

function normalizeType(t?: string): string {
  if (!t) return "string";
  const lower = String(t).toLowerCase();
  if (lower === "object") return "string";
  if (lower === "float64" || lower === "double") return "number";
  return lower;
}

/* -------------------------
   Source node builder
   ------------------------- */

function buildNodesFromSourceJson(json: any, xStart = 0): Node[] {
  const nodes: Node[] = [];
  const tables = json.tables ?? [];

  let currentY = 0; // track accumulated Y offset

  tables.forEach((table: any, tIdx: number) => {
    const tableId = `SRC.${table.name}`;
    const fields = table.columns ?? table.field ?? [];
    const tableHeight = HEADER_HEIGHT + fields.length * ROW_HEIGHT + 48;

    const tableY = currentY; // place this table at accumulated Y
    currentY += tableHeight + 40; // increment for the next table

    // table header node
    nodes.push({
      id: tableId,
      position: { x: xStart, y: tableY },
      data: { label: table.name, fullHeight: tableHeight },
      type: "table",
      style: { width: TABLE_WIDTH, height: tableHeight },
    });

    // columns
    fields.forEach((fld: any, fIdx: number) => {
      const fieldId = `SRC.${table.name}.${fld.name}`;
      nodes.push({
        id: fieldId,
        position: {
          x: xStart + INNER_PADDING,
          y: HEADER_HEIGHT + fIdx * ROW_HEIGHT,
        },
        data: {
          label: `${fld.name}${fld.type ? ` (${normalizeType(fld.type)})` : ""}${fld.pk ? " [PK]" : ""}${fld.fk ? " [FK]" : ""}`,
        },
        type: "default",
        style: {
          border: "1px solid #e5e7eb",
          padding: "2px 6px",
          fontSize: 11,
          background: "#fff",
          width: `calc(${TABLE_WIDTH}px - ${INNER_PADDING * 2}px)`,
          height: ROW_HEIGHT,
          lineHeight: `${ROW_HEIGHT}px`,
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          overflow: "hidden",
        },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        parentNode: tableId,
        extent: "parent",
        draggable: false,
      });
    });
  });

  return nodes;
}

/* -------------------------
   Target node builder (recursive for FieldGroup)
   - builds nodes with ids that match mapping format:
     TGT.{entity}.{path}  where path is like Group-FieldName
   ------------------------- */

function flattenTargetFields(items: any[], parentPath = ""): { idPath: string; label: string }[] {
  const out: { idPath: string; label: string }[] = [];
  (items || []).forEach((it: any) => {
    if (it.eClass === "FieldGroup") {
      const groupName = it.name || it.label || it.id || it.TBL_ID || "Group";
      const newParent = parentPath ? `${parentPath}-${groupName}` : `${groupName}`;
      // add group header (optional)
      out.push({ idPath: newParent, label: groupName });
      const children = flattenTargetFields(it.field || it.fields || [], newParent);
      out.push(...children);
    } else {
      const fldName = it.COLUMN_NAME || it.name || it.FLD_ID || it.FLD_PHYSICAL_ID || "field";
      const idPath = parentPath ? `${parentPath}-${fldName}` : `${fldName}`;
      out.push({ idPath, label: fldName });
    }
  });
  return out;
}

function buildNodesFromTargetJson(json: any, xStart = 800, baseY = 0): Node[] {
  const nodes: Node[] = [];
  const tables = json.tables ?? (Array.isArray(json) ? json : [json]);

  tables.forEach((table: any, tIdx: number) => {
    // table could be an object with name/id and .field array
    const tableName = table.name || table.id || table.TBL_ID || `TGT${tIdx}`;
    const flat = flattenTargetFields(table.field || table.columns || table.field || []);
    const fieldCount = flat.length;
    const tableHeight = HEADER_HEIGHT + Math.max(fieldCount, 1) * ROW_HEIGHT + 24;
    const tableY = baseY + tIdx * (tableHeight + 40);

    // table header
    nodes.push({
      id: `TGT.${tableName}`,
      position: { x: 800, y: tableY },
      data: { label: tableName, fullHeight: tableHeight },
      type: "table",
      style: { width: TABLE_WIDTH, height: tableHeight },
    });

    // create nodes for each flat entry and give id like TGT.{tableName}.{idPath}
    flat.forEach((f, idx) => {
      const fieldId = `TGT.${tableName}.${f.idPath}`;
      nodes.push({
        id: fieldId,
        position: {
          x: INNER_PADDING,
          y: tableY + HEADER_HEIGHT + idx * ROW_HEIGHT,
        },
        data: { label: `${f.label}` },
        type: "default",
        style: {
          border: "1px solid #e5e7eb",
          padding: "2px 6px",
          fontSize: 11,
          background: "#fff",
          width: `calc(${TABLE_WIDTH}px - ${INNER_PADDING * 2}px)`,
          height: ROW_HEIGHT,
          lineHeight: `${ROW_HEIGHT}px`,
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          overflow: "hidden",
        },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        parentNode: `TGT.${tableName}`,
        extent: "parent",
      });
    });
  });

  return nodes;
}

/* -------------------------
   Edge builder from lineage mappings
   - sets data.mappingIndex = index so clicks can find mapping
   ------------------------- */

function buildEdgesFromLineage(
  srcNodes: Node[],
  tgtNodes: Node[],
  mappings: Mapping[],
  filterFn?: (m: Mapping) => boolean
): { edges: Edge[]; transformerNodes: Node[] } {
  const edges: Edge[] = [];
  const transformerNodes: Node[] = [];
  const transformerMap: Record<string, Node> = {}; // reuse lookup

  const findNode = (nodesArr: Node[], id: string) =>
    nodesArr.find((n) => n.id === id);

  mappings.forEach((map: Mapping, idx: number) => {
    if (filterFn && !filterFn(map)) return;

    const srcId = `SRC.${map.source_table}.${map.source_column}`;

    // Handle both plain and prefixed mdm_field
    const tgtCandidate1 = `TGT.${map.mdm_entity}.${map.mdm_field}`;
    const tgtCandidate2 = `TGT.${map.mdm_entity}.${map.mdm_entity}-${map.mdm_field}`;
    const tgtId = findNode(tgtNodes, tgtCandidate1)
      ? tgtCandidate1
      : findNode(tgtNodes, tgtCandidate2)
      ? tgtCandidate2
      : null;

    const srcNode = findNode(srcNodes, srcId);
    const tgtNode = tgtId ? findNode(tgtNodes, tgtId) : null;
    if (!srcNode || !tgtNode) return;

    const srcType = normalizeType(map.source_type);
    const tgtType = normalizeType(map.target_type);
    const requiresTransform = srcType !== tgtType;

    if (requiresTransform) {
      const key = `${srcType}->${tgtType}`;
      let transformerNode = transformerMap[key];

      if (!transformerNode) {
        const transformerId = `transformer-${key}`;

        transformerNode = {
          id: transformerId,
          type: "default",
          position: {
            x: (srcNode.position.x + tgtNode.position.x) / 2 + 400,
            y: 0,
          },
          data: {
            label: `Transform\n${srcType}→${tgtType}`,
          },
          style: {
            background: "#fee2e2",
            border: "1px dashed #ef4444",
            fontSize: 10,
            padding: 4,
            textAlign: "center",
            whiteSpace: "pre-line",
          },
          sourcePosition: Position.Right,
          targetPosition: Position.Left,
          draggable: true,
        };

        transformerNodes.push(transformerNode);
        transformerMap[key] = transformerNode;
      }

      edges.push({
        id: `edge-${idx}-src`,
        source: srcId,
        target: transformerNode.id,
        style: { stroke: "#f59e0b", strokeWidth: 1 },
        markerEnd: { type: MarkerType.ArrowClosed },
        data: { mappingIndex: idx },
      });

      edges.push({
        id: `edge-${idx}-tgt`,
        source: transformerNode.id,
        target: tgtId!,
        style: { stroke: "#f59e0b", strokeWidth: 1 },
        markerEnd: { type: MarkerType.ArrowClosed },
        data: { mappingIndex: idx },
      });
    } else {
      edges.push({
        id: `edge-${idx}`,
        source: srcId,
        target: tgtId!,
        style: { stroke: "#f59e0b", strokeWidth: 1 },
        markerEnd: { type: MarkerType.ArrowClosed },
        data: { mappingIndex: idx, confidence: map.confidence },
      });
    }
  });

  return { edges, transformerNodes };
}





/* -------------------------
   Table node visual (collapsible)
   ------------------------- */

const TableNodeWrapper: React.FC<{ id: string; data: any }> = ({ id, data }) => {
  const [collapsed, setCollapsed] = useState(false);
  // useReactFlow must run inside ReactFlowProvider (we wrap later)
  // But to avoid calling hook here (and causing issues when TableNode is used outside provider),
  // we access window.reactFlow? However, TableNodeWrapper will only be used inside ReactFlow, so it's fine.
  // We'll use a simple collapse purely local for visuals (actual hide is performed by parent-level logic via setNodes in main component).
  return (
    <div style={{ border: "1px solid #8ab6df", borderRadius: 8, background: "#f6fbff", width: TABLE_WIDTH, overflow: "hidden" }}>
      <div
        onClick={() => setCollapsed((c) => !c)}
        style={{
          background: "#0b73c8",
          color: "#fff",
          fontWeight: 600,
          padding: "6px 10px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: HEADER_HEIGHT,
          cursor: "pointer",
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{data.label}</span>
        <span style={{ fontSize: 12 }}>{collapsed ? "▶" : "▼"}</span>
      </div>
    </div>
  );
};

/* -------------------------
   Main Component
   ------------------------- */

const LineageEditor: React.FC<LineageEditorProps> = ({ sourceJson, targetJson, lineage, height = 800 }) => {
  // default to sample data if not provided
  const source = sourceJson ?? DEFAULT_SOURCE_JSON;
  const target = targetJson ?? DEFAULT_TARGET_JSON;
  const lineageInput =
    (lineage && ((lineage as any).mappings ? (lineage as any).mappings : lineage)) ??
    DEFAULT_LINEAGE.mappings;

  // local editable mapping list (deep copy + id)
  const [mappings, setMappings] = useState<Mapping[]>(() =>
    (lineageInput || []).map((m: Mapping, i: number) => {
      const copy = { ...m };
      // ensure notes severity are typed as Severity
      if (copy.notes) {
        copy.notes = copy.notes.map((n) => ({
          ...n,
          severity: (["info", "warning", "error"].includes(n.severity as string)
            ? (n.severity as Severity)
            : "info"),
        }));
      }
      copy.id = copy.id ?? `map-${i}`;
      return copy;
    })
  );

  // filter states
  const [filterTags, setFilterTags] = useState<string[]>([]);
  const [filterSeverities, setFilterSeverities] = useState<Severity[]>([]);
  const [filterCategories, setFilterCategories] = useState<string[]>([]);
  const [filterActions, setFilterActions] = useState<string[]>([]);

  // inspector active mapping (index in mappings)
  const [activeMappingIndex, setActiveMappingIndex] = useState<number | null>(null);

  // nodes for source & target (memoized)
  const srcNodesBase = useMemo(() => buildNodesFromSourceJson(source, 0), [source]);
  const tgtNodesBase = useMemo(() => buildNodesFromTargetJson(target, 900), [target]);

  // available filters derived from mappings
  const availableFilters = useMemo(() => {
    const tags = new Set<string>();
    const categories = new Set<string>();
    const severities = new Set<Severity>();
    const actions = new Set<string>();
    mappings.forEach((m) => {
      (m.notes || []).forEach((n) => {
        if (n.tag) tags.add(n.tag);
        if (n.category) categories.add(n.category);
        if (n.severity) severities.add(n.severity);
        if (n.action) actions.add(n.action);
      });
    });
    return {
      tags: Array.from(tags).sort(),
      categories: Array.from(categories).sort(),
      severities: Array.from(severities).sort(),
      actions: Array.from(actions).sort(),
    };
  }, [mappings]);

  // mapping filter predicate
  const mappingMatchesFilters = useCallback(
    (m: Mapping) => {
      const anyFilters =
        filterTags.length ||
        filterSeverities.length ||
        filterCategories.length ||
        filterActions.length;
      if (!anyFilters) return true;
      const notes = m.notes || [];
      if (!notes.length) return false;

      return notes.some((n) => {
        if (filterTags.length && !filterTags.includes(n.tag)) return false;
        if (filterSeverities.length && !filterSeverities.includes(n.severity)) return false;
        if (filterCategories.length && !(n.category && filterCategories.includes(n.category)))
          return false;
        if (filterActions.length && !(n.action && filterActions.includes(n.action))) return false;
        return true;
      });
    },
    [filterTags, filterSeverities, filterCategories, filterActions]
  );

  // edges + transformer nodes
  const { edges: edgesComputed, transformerNodes } = useMemo(
    () =>
      buildEdgesFromLineage(srcNodesBase, tgtNodesBase, mappings, mappingMatchesFilters),
    [srcNodesBase, tgtNodesBase, mappings, mappingMatchesFilters]
  );

  // state
  const [nodesState, setNodesState] = useState<Node[]>([
    ...srcNodesBase,
    ...tgtNodesBase,
    ...transformerNodes,
  ]);
  const [edgesState, setEdgesState] = useState<Edge[]>(edgesComputed);

  // keep nodes updated when inputs change
  useEffect(() => {
    setNodesState([...srcNodesBase, ...tgtNodesBase, ...transformerNodes]);
  }, [srcNodesBase, tgtNodesBase, transformerNodes]);

  // keep edges updated when computed edges change
  useEffect(() => {
    const highlightedEdges = edgesComputed.map((e) => {
      const mappingIndex = e.data?.mappingIndex;
      if (mappingIndex !== undefined && mappingIndex === activeMappingIndex) {
        return {
          ...e,
          style: { ...(e.style || {}), stroke: "#2563eb", strokeWidth: 1.5 },
        };
      }
      return e;
    });
    setEdgesState(highlightedEdges);
  }, [edgesComputed, activeMappingIndex]);

  // ResizeObserver suppression (both common error messages)
  useEffect(() => {
    const resizeObserverErr = /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/;
    const errHandler = (e: ErrorEvent) => {
      if (resizeObserverErr.test(e.message)) {
        e.stopImmediatePropagation();
      }
    };
    const rejHandler = (e: PromiseRejectionEvent) => {
      if (e.reason && e.reason.message && resizeObserverErr.test(e.reason.message)) {
        e.preventDefault();
      }
    };
    window.addEventListener("error", errHandler);
    window.addEventListener("unhandledrejection", rejHandler as any);
    return () => {
      window.removeEventListener("error", errHandler);
      window.removeEventListener("unhandledrejection", rejHandler as any);
    };
  }, []);

  // mapping edit helpers
  const updateMappingAt = (idx: number, patch: Partial<Mapping>) => {
    setMappings((prev) => prev.map((m, i) => (i === idx ? { ...m, ...patch } : m)));
  };

  const updateNote = (mapIdx: number, noteIdx: number, patch: Partial<Note>) => {
    setMappings((prev) =>
      prev.map((m, i) => {
        if (i !== mapIdx) return m;
        const notes = (m.notes || []).map((n, j) => (j === noteIdx ? { ...n, ...patch } : n));
        return { ...m, notes };
      })
    );
  };

  const addNote = (mapIdx: number) => {
    const note: Note = { tag: "manual", severity: "info", category: "lineage", action: "review", message: "" };
    setMappings((prev) => prev.map((m, i) => (i === mapIdx ? { ...m, notes: [...(m.notes || []), note] } : m)));
  };

  const removeNote = (mapIdx: number, noteIdx: number) => {
    setMappings((prev) => prev.map((m, i) => (i === mapIdx ? { ...m, notes: (m.notes || []).filter((_n, j) => j !== noteIdx) } : m)));
  };

  // handle edge click
  const onEdgeClick = (_event: any, edge: Edge) => {
    const mappingIndex = edge.data?.mappingIndex;
    if (mappingIndex === undefined || mappingIndex === null) {
      setActiveMappingIndex(null);
      return;
    }
    setActiveMappingIndex(mappingIndex);
  };

  // actionable counts
  const actionableCounts = useMemo(() => {
    const counts = { total: mappings.length, notesWithSeverity: { info: 0, warning: 0, error: 0 }, mappingsWithNotes: 0 };
    mappings.forEach((m) => {
      if (m.notes && m.notes.length) {
        counts.mappingsWithNotes += 1;
        (m.notes || []).forEach((n) => {
          counts.notesWithSeverity[n.severity] = (counts.notesWithSeverity as any)[n.severity] + 1;
        });
      }
    });
    return counts;
  }, [mappings]);

  // Save function (hook up to backend as needed)
  const saveAll = () => {
    // This is the place to submit the edited mappings back to server
    console.log("Saved mappings:", mappings);
    alert("Saved mappings (check console). Hook saveAll to backend as required.");
  };

  // Some options for inspector selects
  const SeverityOptions: Severity[] = ["info", "warning", "error"];
  const CategoryOptions = ["lineage", "semantic", "dq", "standardization", "lookup"];
  const ActionOptions = ["review", "add_rule", "harmonize", "drop_mapping"];

  /* -------------------------
     Render
     ------------------------- */

  const selectedMapping = activeMappingIndex !== null ? mappings[activeMappingIndex] : null;

  return (
    <Container fluid style={{ padding: 8 }}>
      <Card className="mb-2">
        <Card.Body style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <div>
            <strong>Actionable Dashboard</strong>
            <div className="small text-muted">Quick filters and counts</div>
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Badge bg="secondary">Mappings: {actionableCounts.total}</Badge>
            <Badge bg="info">With Notes: {actionableCounts.mappingsWithNotes}</Badge>
            <Badge bg="warning">Warnings: {actionableCounts.notesWithSeverity.warning}</Badge>
            <Badge bg="danger">Errors: {actionableCounts.notesWithSeverity.error}</Badge>
          </div>

          <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
            <Form.Select
              size="sm"
              value={filterSeverities.join(",")}
              onChange={(e) => setFilterSeverities(e.target.value ? (e.target.value.split(",") as Severity[]) : [])}
              style={{ minWidth: 160 }}
            >
              <option value="">Severity (All)</option>
              {availableFilters.severities.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Form.Select>

            <Form.Select
              size="sm"
              value={filterTags.join(",")}
              onChange={(e) => setFilterTags(e.target.value ? e.target.value.split(",") : [])}
              style={{ minWidth: 160 }}
            >
              <option value="">Tag (All)</option>
              {availableFilters.tags.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Form.Select>

            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => {
                setFilterTags([]);
                setFilterSeverities([]);
                setFilterCategories([]);
                setFilterActions([]);
              }}
            >
              Clear Filters
            </Button>

            <Button size="sm" variant="success" onClick={saveAll}>
              Save
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Row>
        <Col md={9} style={{ height }}>
          <Card style={{ height: "100%" }}>
            <Card.Body style={{ height: "100%", padding: 8 }}>
              <ReactFlowProvider>
                <ReactFlow
                  nodes={nodesState}
                  edges={edgesState}
                  nodeTypes={{ table: TableNodeWrapper }}
                  onNodesChange={(changes: NodeChange[]) => setNodesState((nds) => applyNodeChanges(changes, nds))}
                  onEdgesChange={(changes: EdgeChange[]) => setEdgesState((eds) => applyEdgeChanges(changes, eds))}
                  onConnect={(c: Connection) => setEdgesState((eds) => addEdge({ ...c, markerEnd: { type: MarkerType.ArrowClosed } }, eds))}
                  onEdgeClick={onEdgeClick}
                  fitView={false} /* we call fitView after init (delayed) */
                  onInit={(flowInstance: any) => {
                    // delayed fitView to avoid ResizeObserver issues
                    setTimeout(() => {
                      try {
                        flowInstance.fitView({ padding: 0.1 });
                      } catch (err) {
                        // ignore
                      }
                    }, 250);
                  }}
                  connectionLineStyle={{ stroke: "#888", strokeWidth: 1.2 }}
                  style={{ width: "100%", height: "100%" }}
                >
                  <Background />
                  <Controls />
                </ReactFlow>
              </ReactFlowProvider>
            </Card.Body>
          </Card>
        </Col>

        {/* Inspector */}
        <Col md={3}>
          <Card>
            <Card.Header>
              <strong>Inspector</strong>
            </Card.Header>
            <Card.Body style={{ maxHeight: height - 80, overflowY: "auto" }}>
              {!selectedMapping ? (
                <div className="text-muted">Click an edge to inspect mapping</div>
              ) : (
                <>
                  <div>
                    <div className="fw-bold">
                      {selectedMapping.mdm_entity} → {selectedMapping.mdm_field}
                    </div>
                    <div className="small text-muted">
                      {selectedMapping.source_table}.{selectedMapping.source_column} ↔ {selectedMapping.target_type || selectedMapping.target_type}
                    </div>
                    <div className="mt-2">
                      <Form.Label className="small">Confidence</Form.Label>
                      <Form.Select
                        size="sm"
                        value={selectedMapping.confidence || ""}
                        onChange={(e) => updateMappingAt(activeMappingIndex!, { confidence: e.target.value })}
                      >
                        <option value="">(unset)</option>
                        <option>High</option>
                        <option>Medium</option>
                        <option>Low</option>
                      </Form.Select>
                    </div>
                  </div>

                  <Tabs defaultActiveKey="notes" className="mt-3" mountOnEnter>
                    <Tab eventKey="notes" title={`Notes (${(selectedMapping.notes || []).length})`}>
                      <div className="mt-2">
                        <Button size="sm" onClick={() => addNote(activeMappingIndex!)} className="mb-2">
                          + Add Note
                        </Button>
                        <ListGroup variant="flush">
                          {(selectedMapping.notes || []).map((note, ni) => (
                            <ListGroup.Item key={ni}>
                              <div className="d-flex gap-2 align-items-start">
                                <div style={{ flex: 1 }}>
                                  <InputGroup size="sm" className="mb-1">
                                    <InputGroup.Text>Tag</InputGroup.Text>
                                    <Form.Control size="sm" value={note.tag} onChange={(e) => updateNote(activeMappingIndex!, ni, { tag: e.target.value })} />
                                  </InputGroup>

                                  <div className="d-flex gap-1">
                                    <Form.Select size="sm" value={note.severity} onChange={(e) => updateNote(activeMappingIndex!, ni, { severity: e.target.value as Severity })}>
                                      {SeverityOptions.map((s) => (
                                        <option key={s} value={s}>
                                          {s}
                                        </option>
                                      ))}
                                    </Form.Select>

                                    <Form.Select size="sm" value={note.category || ""} onChange={(e) => updateNote(activeMappingIndex!, ni, { category: e.target.value })}>
                                      <option value="">category</option>
                                      {CategoryOptions.map((c) => (
                                        <option key={c} value={c}>
                                          {c}
                                        </option>
                                      ))}
                                    </Form.Select>

                                    <Form.Select size="sm" value={note.action || ""} onChange={(e) => updateNote(activeMappingIndex!, ni, { action: e.target.value })}>
                                      <option value="">action</option>
                                      {ActionOptions.map((a) => (
                                        <option key={a} value={a}>
                                          {a}
                                        </option>
                                      ))}
                                    </Form.Select>
                                  </div>

                                  <Form.Control as="textarea" rows={3} className="mt-2" value={note.message} onChange={(e) => updateNote(activeMappingIndex!, ni, { message: e.target.value })} />
                                </div>

                                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                  <Button size="sm" variant="danger" onClick={() => removeNote(activeMappingIndex!, ni)}>
                                    Delete
                                  </Button>
                                </div>
                              </div>
                            </ListGroup.Item>
                          ))}
                        </ListGroup>
                      </div>
                    </Tab>

                    <Tab eventKey="harmonize" title="Harmonization">
                      <div className="mt-2">
                        <Form.Group className="mb-2">
                          <Form.Label className="small">Suggested Harmonization</Form.Label>
                          <Form.Control as="textarea" rows={5} placeholder="Add harmonization notes or rules..." />
                        </Form.Group>
                        <Button size="sm">Add Harmonization Rule</Button>
                      </div>
                    </Tab>

                    <Tab eventKey="validation" title="Validation">
                      <div className="mt-2">
                        <Form.Group>
                          <Form.Label className="small">Suggested Validation</Form.Label>
                          <Form.Control as="textarea" rows={5} placeholder="Add validation rules e.g. not-null, regex..." />
                        </Form.Group>
                        <Button size="sm" className="mt-2">
                          Add Validation Rule
                        </Button>
                      </div>
                    </Tab>
                  </Tabs>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default LineageEditor;
